import { createTheme, SxProps } from '@mui/material';
import { CSSProperties } from 'react';
import { Theme } from '@mui/system';
import { BreakpointOverrides } from '@mui/material/styles';
import { Breakpoint } from '@mui/system/createTheme/createBreakpoints';

declare module '@mui/material/styles/createTheme' {
  type constsSize = { ['size1']: string };
  type constsType = {
    desktop: constsSize;
    laptop: constsSize;
    tablet: constsSize;
  };

  interface Theme {
    sidebarWidth: number;
    sidebarMobileHeight: number;
    consts: constsType;
  }

  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    sidebarWidth?: number;
    sidebarMobileHeight?: number;
    consts: constsType;
  }
}

interface ColorIdentifier {
  [key: string]: string;
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    // removes the `default` breakpoint
    // xs: true
    // sm: false
    // md: false
    // lg: false
    // xl: false
    // adds the `custom` breakpoint
    mobile: false;
    tablet: true;
    laptop: true;
    desktop: true;
    largeDesktop: true;
    desktopXL: true;
    desktopXXL: true;
  }

  interface BreakpointsOptions {
    media?: {
      [key in Breakpoint | 'desktopXLToDesktop']?: string;
    };
  }

  interface Breakpoints {
    media?: {
      [key in Breakpoint | 'desktopXLToDesktop']?: string;
    };
  }

  interface PaletteOptions {
    neutral?: ColorIdentifier;
    myPrimary?: ColorIdentifier;
    mySecondary?: ColorIdentifier;
    mainControls?: ColorIdentifier;
    propVars?: ColorIdentifier;
  }

  interface Palette {
    neutral?: ColorIdentifier;
    myPrimary?: ColorIdentifier;
    mySecondary?: ColorIdentifier;
    mainControls?: ColorIdentifier;
    propVars?: { [key: string]: string };
  }

  interface TypographyVariants {
    [key: string]: CSSProperties | string;
  }

  interface TypographyVariantsOptions {
    [key: string]: CSSProperties | string;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    fs16: true;
  }
}

export default createTheme({
  consts: {
    desktop: {
      size1: 'calc(calc(1vw + 1vh) *  0.034662)', // 1px
    },
    laptop: {
      size1: 'calc(calc(1vw + 1vh) * 0.05581)', // 1px
    },
    tablet: {
      size1: 'calc(calc(1vw + 1vh) * 0.05581)', // 1px
    },
  },

  typography: {
    fontFamily: ['Open Sans', 'Ubuntu', 'Actor'].join(','),
    h1: {
      fontSize: '1.25em',
      fontWeight: 'normal',
    },
    h3: {
      fontSize: '2.46vmin', // 24px
      lineHeight: '2.88vmin',
      fontWeight: 'normal',
    },
    h4: {
      fontSize: '2vmin', // 20px
      fontWeight: 'normal',
    },
    h5: {
      fontSize: '1.95vmin', // 19px
      fontWeight: 'normal',
    },
    h6: {
      fontSize: 'calc(calc(1vw + 1vh) * 0.61)', // 16px
      fontWeight: 'bold',
    },
    caption: {
      fontSize: '1.64vmin', // 16px
      fontWeight: 'normal',
    },
    subtitle1: {
      fontSize: '1em',
      fontWeight: 'normal',
    },
    body1: {
      fontSize: '.88em', // 14px
    },
    body2: {
      fontSize: '1.44vmin', // 14px
    },
    fs11: 'calc(calc(1vw + 1vh) * 0.39)', // 12px
    fs12: 'calc(calc(1vw + 1vh) * 0.43)', // 12px
    fs13: 'calc(calc(1vw + 1vh) * 0.47)', // 13px
    fs14: 'calc(calc(1vw + 1vh) * 0.48)', // 14px
    fs16: 'calc(calc(1vw + 1vh) * 0.61)', // 16px
    fs19: 'calc(calc(1vw + 1vh) * 0.66)', // 19px
    fs24: 'calc(calc(1vw + 1vh) * 0.83)', // 24px
    mainFontFamily: 'Open Sans',
    // secondaryFontFamily: 'Actor',
    additionalFontFamily: 'Ubuntu',
  },
  palette: {
    // mode: 'dark',
    primary: {
      main: '#F22F46',
    },
    neutral: {
      white: '#F9FBFF',
      grey4: '#F6F6F6',
      grey8: '#EDEDED',
      grey10: '#E8E8E9',
      grey15: '#DDDDDE',
      grey20: '#D2D2D3',
      grey30: '#BBBBBD',
      grey50: '#8D8E90',
      greyRGBA50: 'rgba(57, 59, 55, 0.5)',
      grey70: '#393B37',
      lightGrey80: '#393B37CC',
      grey80: '#494A4E',
      greyRGBA80: 'rgba(57, 59, 55, 0.8)',
      black: '#1C1D22',
      error: '#FFA1A1',
    },
    myPrimary: {
      blue: '#B8E7F0',
      white: '#FFFFFF',
      white15: '#FFFFFF26',
      lightGrey: '#F0F9FC',
      gradient: 'linear-gradient(100.09deg, #4054B2 0%, #6EC1E4 100%);',
      lightBlue: '#6EC1E4',
      lightBlue20: '#D3E9F1',
      lightBlue30: '#D6EEF7',
      lightBlue40: '#A7D3E3',
      lightBlue50: '#C7E4EF',
      lightBlue70: '#65B3CF',
      lightBlue100: '#2392BA',
      darkBlue50: '#2B93B8',
      darkBlue70: '#588595',
      darkBlue100: '#1F7EA1',
      darkAquamarine: '#17607A',
      hover: '#1D85AB',
    },
    mainControls: {
      mainBlue: '#517ec5',
      lightBlue: '#AFC1DD',
      blueGradient: 'linear-gradient(0deg, #517ec5 0%, #6bbbe1 100%)',
      subTitleColor: '#494a4e',
      almostWhite: '#F5F5F5',
      activeText: '#494a4e',
      backText: '#676767',
      lightGrey: '#DDDDDE',
      inActivegrey: '#ededed',
      syan: '#83B1C1',
    },
    // property vars
    propVars: {
      index: 'calc(1vw + 1vh)',
      borderRadius: '12px',
      actorFamily: 'Actor',
      regularFontWeight: '400',
      semiBoldFontWeight: '500',
      linearTransition: 'all .3s linear',
      easeInOutTransition: 'all .3s ease-in-out',
      btnBoxShadow: '0px 5px 20px rgba(0, 0, 0, 0.05)',
    },
    background: {
      default: '#303030',
    },
  },

  breakpoints: {
    media: {
      // largeDesktop: '',
      // desktopXXL: '',
      // mobile: '',
      tablet: '@media screen and (max-width: 768px)',
      laptop: '@media screen and (max-width: 1024px)',
      desktop: '@media screen and (max-width: 1441px)',
      desktopXLToDesktop: '@media screen and (max-width: 2560px) and (min-width: 1441px)',
      desktopXL: '@media screen and (max-width: 2560px)',
    },
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      // mobile: 0,
      tablet: 640,
      laptop: 1024,
      desktop: 1441,
      largeDesktop: 1921,
      desktopXL: 2560,
      desktopXXL: 3840,
    },
  },

  sidebarWidth: 260,
  sidebarMobileHeight: 90,
});

// export default theme
